import { Button, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { InvoiceMissionResponse } from 'src/shared/models/invoiceMission/invoiceMission';
import { useParams } from 'react-router-dom';
import { InvoiceSendingTypeIntegerEnum } from 'src/shared/models/enums/invoiceSendingTypeInteger.enum';
import { InvoiceSendingTypeStringEnum } from 'src/shared/models/enums/InvoiceSendingTypeString.enum';
import { useState } from 'react';
import { InvoiceMissionModals } from './invoiceMissionModals';
import { InvoiceMissionModalKeys, useInvoiceMissionModalState } from './useInvoiceMissionModalState';
import { InvoiceMissionReminderButton } from './invoiceMissionReminderButton';
import { InvoiceMissionApproveButtons } from './invoiceMissionApproveButtons';
import { InvoiceMissionInvoicesGeneratingIndicator } from './invoiceMissionInvoicesGeneratingIndicator';

type SendingSessionSectionBtnHandlerProps = {
  loading: boolean;
  invoiceMission: InvoiceMissionResponse;
  refetchMission: (refetch: boolean) => void;
  onDeleteReminderSending?: () => void;
};

export const SendingSessionSectionBtnHandler = (props: SendingSessionSectionBtnHandlerProps) => {
  const { loading, invoiceMission, refetchMission, onDeleteReminderSending } = props;
  const { t } = useTranslation();
  const { sendingType } = useParams<{ sendingType: string }>();
  const [isGeneratingInvoices, setIsGeneratingInvoices] = useState(true);
  const parsedSendingType = Number(sendingType);

  const modalState = useInvoiceMissionModalState();

  const mainData = invoiceMission.dataList?.find((data) => data.sendingType === InvoiceSendingTypeStringEnum.invoice);
  const reminder1Data = invoiceMission.dataList?.find((data) => data.sendingType === InvoiceSendingTypeStringEnum.reminder1);
  const reminder2Data = invoiceMission.dataList?.find((data) => data.sendingType === InvoiceSendingTypeStringEnum.reminder2);

  return (
    <>
      <Grid container direction='row' alignItems='center'>
        {mainData?.approval.approvalRequired && !mainData.approval.approvedAt && parsedSendingType === InvoiceSendingTypeIntegerEnum.Main ? (
          <Grid item sx={{ display: 'flex', gap: 2 }}>
            <InvoiceMissionApproveButtons
              invoiceMissionId={invoiceMission.invoiceMission_id}
              sendingType={parsedSendingType}
              refetchMission={refetchMission}
            />
          </Grid>
        ) : (
          <Grid item sx={{ display: 'flex', gap: 2 }}>
            {parsedSendingType === InvoiceSendingTypeIntegerEnum.Main && invoiceMission.data.totalCount > 0 && (
              <>
                {isGeneratingInvoices && mainData?.approval.createStartedAt && !mainData?.approval.createEndedAt ? (
                  <InvoiceMissionInvoicesGeneratingIndicator
                    invoiceMissionId={invoiceMission.invoiceMission_id}
                    sendingType={parsedSendingType}
                    setIsGeneratingInvoices={(val: boolean) => setIsGeneratingInvoices(val)}
                    refetchMission={refetchMission}
                  />
                ) : (
                  <Button
                    size='medium'
                    variant='outlined'
                    disabled={loading}
                    onClick={() => modalState.setModal(InvoiceMissionModalKeys.DeliveryMethod, true)}
                  >
                    {t('invoice:invoiceMissionPage.sendSending')}
                  </Button>
                )}
              </>
            )}
            {parsedSendingType === InvoiceSendingTypeIntegerEnum.Reminder1 && (
              <>
                {isGeneratingInvoices && reminder1Data?.approval.createStartedAt && !reminder1Data?.approval.createEndedAt ? (
                  <InvoiceMissionInvoicesGeneratingIndicator
                    invoiceMissionId={invoiceMission.invoiceMission_id}
                    sendingType={parsedSendingType}
                    setIsGeneratingInvoices={(val: boolean) => setIsGeneratingInvoices(val)}
                    refetchMission={refetchMission}
                  />
                ) : (
                  <InvoiceMissionReminderButton
                    reminder={1}
                    prevoiusSendingHasBeenSent={mainData?.totalCountSent ?? 0}
                    totalCount={reminder1Data?.totalCount || 0}
                    totalCountSent={reminder1Data?.totalCountSent || 0}
                    disableCreate={!reminder1Data?.isTemplateSendingValid}
                    approvalRequired={reminder1Data?.approval.approvalRequired ?? true}
                    isApproved={Boolean(reminder1Data?.approval.approvedAt)}
                    invoiceMissionId={invoiceMission.invoiceMission_id}
                    loading={loading}
                    setOpenDeliveryMethodModal={() => modalState.setModal(InvoiceMissionModalKeys.DeliveryMethod, true)}
                    setOpenCreateInvoiceReminderModal={() => modalState.setModal(InvoiceMissionModalKeys.CreateReminder, true)}
                    refetchMission={refetchMission}
                  />
                )}
                {/* Backa påminnelse 1: syns bara när påminnelsen faktiskt är skapad (approval-id finns). */}
                {!!reminder1Data?.approval?.id && onDeleteReminderSending && (
                  <Button size='medium' variant='outlined' color='error' disabled={loading} onClick={onDeleteReminderSending}>
                    {t('invoice:invoiceMissionPage.deleteReminderSending', { number: 1 })}
                  </Button>
                )}
              </>
            )}

            {parsedSendingType === InvoiceSendingTypeIntegerEnum.Reminder2 && (
              <>
                {isGeneratingInvoices && reminder2Data?.approval.createStartedAt && !reminder2Data?.approval.createEndedAt ? (
                  <InvoiceMissionInvoicesGeneratingIndicator
                    invoiceMissionId={invoiceMission.invoiceMission_id}
                    sendingType={parsedSendingType}
                    setIsGeneratingInvoices={(val: boolean) => setIsGeneratingInvoices(val)}
                    refetchMission={refetchMission}
                  />
                ) : (
                  <InvoiceMissionReminderButton
                    reminder={2}
                    prevoiusSendingHasBeenSent={reminder1Data?.totalCountSent ?? 0}
                    totalCount={reminder2Data?.totalCount || 0}
                    totalCountSent={reminder2Data?.totalCountSent || 0}
                    disableCreate={!reminder2Data?.isTemplateSendingValid}
                    approvalRequired={reminder2Data?.approval.approvalRequired ?? true}
                    isApproved={Boolean(reminder2Data?.approval.approvedAt)}
                    invoiceMissionId={invoiceMission.invoiceMission_id}
                    loading={loading}
                    setOpenDeliveryMethodModal={() => modalState.setModal(InvoiceMissionModalKeys.DeliveryMethod, true)}
                    setOpenCreateInvoiceReminderModal={() => modalState.setModal(InvoiceMissionModalKeys.CreateReminder, true)}
                    refetchMission={refetchMission}
                  />
                )}
                {/* Backa påminnelse 2: syns bara när påminnelsen faktiskt är skapad (approval-id finns). */}
                {!!reminder2Data?.approval?.id && onDeleteReminderSending && (
                  <Button size='medium' variant='outlined' color='error' disabled={loading} onClick={onDeleteReminderSending}>
                    {t('invoice:invoiceMissionPage.deleteReminderSending', { number: 2 })}
                  </Button>
                )}
              </>
            )}
          </Grid>
        )}
      </Grid>

      <InvoiceMissionModals invoiceMission={invoiceMission} parsedSendingType={parsedSendingType} modalState={modalState} />
    </>
  );
};
